import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox } from 'ncoded-component-library';
import {
  DataRoomRFICategory,
  RFI_SEND_REMINDER_TRIGGER,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import useRfiSendCategoriesReminder from 'router/subrouters/DataRoom/hooks/useRfiSendCategoriesReminder';
import BellIcon from 'icons/Bell.icon';
import AddProfileIcon from 'icons/AddProfile.icon';
import ResolvePopup from 'components/ResolvePopup';
import {
  CategoryCellProps,
  NameCell,
  FilesCell,
  UploadedOnCell,
  StatusCell,
} from '../../../data/sharedCells';

type CellProps = CategoryCellProps & {
  isSelected?: boolean;
  isSelectedMode?: boolean;
  isUploaded?: boolean;
  isReassignEnabled?: boolean;
  onSelect?: (category: DataRoomRFICategory) => void;
  onCategoryUpdate: (category: DataRoomRFICategory) => void;
  onReassignClick?: (category: DataRoomRFICategory) => void;
};

export type RfiSpreadsheetCategoryTableRow = Pick<
  DataRoomRFICategory,
  'name' | 'assignedTo'
> &
  Record<'checkbox' | 'files' | 'uploadedOn' | 'status' | 'actions', string>;

export const TABLE_COLUMNS: Array<keyof RfiSpreadsheetCategoryTableRow> = [
  'checkbox',
  'name',
  'assignedTo',
  'files',
  'uploadedOn',
  'status',
  'actions',
];

export const TABLE_COLUMNS_WITHOUT_CHECKBOX = TABLE_COLUMNS.filter(
  (col) => col !== 'checkbox',
);

export const HEADING_TRANSLATIONS: Record<
  keyof RfiSpreadsheetCategoryTableRow,
  string
> = {
  checkbox: '',
  name: 'document',
  assignedTo: 'assignedTo',
  files: 'uploadedFile',
  uploadedOn: 'uploadedOn',
  status: 'status',
  actions: 'actions',
};

export const SORTING_VALUES: Partial<
  Record<keyof RfiSpreadsheetCategoryTableRow, string>
> = {
  name: 'name',
  assignedTo: 'assignedTo',
  uploadedOn: 'uploadedOn',
  status: 'uploadedStatus',
};

export const CUSTOM_COL_SIZES: Partial<
  Record<keyof RfiSpreadsheetCategoryTableRow, number>
> = {
  checkbox: 60,
  name: 240,
  assignedTo: 200,
  files: 260,
  uploadedOn: 150,
  status: 130,
  actions: 100,
};

const CheckboxCell: React.FC<CellProps> = ({
  category,
  isSelected,
  isUploaded,
  onSelect,
}) => (
  <div onClick={(e) => e.stopPropagation()}>
    <Checkbox
      checked={isSelected}
      disabled={isUploaded}
      onChange={() => onSelect?.(category)}
    />
  </div>
);

const AssignedToCell: React.FC<CellProps> = ({ category }) => (
  <p>{category.assignedTo || '/'}</p>
);

const NameWithStatusCell: React.FC<CellProps> = (cellProps) => {
  const { category, isUploaded } = cellProps;

  const { t } = useTranslation();

  return (
    <div>
      <NameCell {...cellProps} />

      {isUploaded && (
        <p className="rfi-categories-table__uploaded-note">
          {t('categoryAlreadyUploadedStaysWith', {
            email: category.assignedTo,
          })}
        </p>
      )}
    </div>
  );
};

const ActionsCell: React.FC<CellProps> = ({
  category,
  isSelectedMode,
  isUploaded,
  isReassignEnabled,
  onCategoryUpdate,
  onReassignClick,
}) => {
  const { t } = useTranslation();

  const { hasRecipient, sendCategoriesReminder } = useRfiSendCategoriesReminder(
    [category],
    RFI_SEND_REMINDER_TRIGGER.DOCUMENTS_TABLE,
    onCategoryUpdate,
  );

  if (isUploaded) return null;

  const disabledTooltip = t('clearSelectionToActOnSingleDocument');

  return (
    <div
      className="rfi-categories-table__actions"
      onClick={(e) => e.stopPropagation()}
    >
      {hasRecipient && (
        <ResolvePopup
          onHover
          popupContent={isSelectedMode ? disabledTooltip : t('sendReminder')}
        >
          <Button
            variant="icon"
            icon={BellIcon}
            disabled={isSelectedMode}
            aria-label={t('sendReminder')}
            onClick={sendCategoriesReminder}
          />
        </ResolvePopup>
      )}

      {isReassignEnabled && (
        <ResolvePopup
          onHover
          popupContent={isSelectedMode ? disabledTooltip : t('reassign')}
        >
          <Button
            variant="icon"
            icon={<AddProfileIcon strokeWidth={1.6} />}
            disabled={isSelectedMode}
            aria-label={t('reassign')}
            onClick={() => onReassignClick?.(category)}
          />
        </ResolvePopup>
      )}
    </div>
  );
};

export const BODY_CONTENT_GENERATE: Record<
  keyof RfiSpreadsheetCategoryTableRow,
  (cellProps: CellProps) => ReactNode
> = {
  checkbox: (cellProps) => <CheckboxCell {...cellProps} />,
  name: (cellProps) => <NameWithStatusCell {...cellProps} />,
  assignedTo: (cellProps) => <AssignedToCell {...cellProps} />,
  files: (cellProps) => <FilesCell {...cellProps} />,
  uploadedOn: (cellProps) => <UploadedOnCell {...cellProps} />,
  status: (cellProps) => <StatusCell {...cellProps} />,
  actions: (cellProps) => <ActionsCell {...cellProps} />,
};
